import { apiRequest } from './client';
import { Submission } from '../types';

export function acceptTask(token: string, taskId: string) {
  return apiRequest<Submission>(`/submissions/accept/${taskId}`, { method: 'POST', token });
}

export interface MySubmissionsResult {
  submissions: Submission[];
  total: number;
  page: number;
  pageSize: number;
}

export function mySubmissions(token: string, page = 1, pageSize = 50) {
  return apiRequest<MySubmissionsResult>(`/submissions/mine?page=${page}&pageSize=${pageSize}`, { token });
}

export function getSubmission(token: string, id: string) {
  return apiRequest<Submission>(`/submissions/${id}`, { token });
}

export function pendingSubmissions(token: string) {
  return apiRequest<Submission[]>('/submissions/pending', { token });
}

export function cancelSubmission(token: string, submissionId: string) {
  return apiRequest<Submission>(`/submissions/${submissionId}/cancel`, { method: 'PATCH', token });
}

export function checkin(token: string, submissionId: string, latitude: number, longitude: number) {
  return apiRequest<Submission>(`/submissions/${submissionId}/checkin`, {
    method: 'PATCH',
    token,
    body: { latitude, longitude },
  });
}

/** photoUri is a local file:// URI, e.g. from expo-camera's takePictureAsync() (native).
 * On web, pass the real File from the browser's camera/file input instead —
 * a plain URI string is meaningless to the browser's fetch/FormData, which
 * needs an actual Blob/File object to upload real file content. */
export function uploadPhoto(token: string, submissionId: string, photoUri: string, file?: File) {
  const form = new FormData();
  if (file) {
    form.append('photo', file, file.name || `photo-${Date.now()}.jpg`);
  } else {
    const filename = photoUri.split('/').pop() ?? `photo-${Date.now()}.jpg`;
    const ext = filename.split('.').pop()?.toLowerCase();
    const mime = ext === 'png' ? 'image/png' : 'image/jpeg';
    // React Native's fetch/FormData accepts this {uri, name, type} shape for file parts.
    form.append('photo', { uri: photoUri, name: filename, type: mime } as unknown as Blob);
  }

  return apiRequest<{ id: string; url: string }>(`/submissions/${submissionId}/photos`, {
    method: 'POST',
    token,
    form,
  });
}

export function submitSubmission(
  token: string,
  submissionId: string,
  confirmedReviewPosted: boolean,
  googleReviewUrl?: string,
  reviewerNote?: string,
  referredCustomerName?: string,
  referredCustomerPhone?: string,
) {
  return apiRequest<Submission>(`/submissions/${submissionId}/submit`, {
    method: 'PATCH',
    token,
    body: {
      confirmedReviewPosted,
      googleReviewUrl: googleReviewUrl || undefined,
      reviewerNote,
      referredCustomerName,
      referredCustomerPhone,
    },
  });
}

export function reviewSubmission(
  token: string,
  submissionId: string,
  approve: boolean,
  rejectReason?: string,
) {
  return apiRequest<Submission>(`/submissions/${submissionId}/review`, {
    method: 'PATCH',
    token,
    body: { approve, rejectReason },
  });
}

export function disputeSubmission(token: string, submissionId: string, reason: string) {
  return apiRequest<Submission>(`/submissions/${submissionId}/dispute`, {
    method: 'PATCH',
    token,
    body: { reason },
  });
}
